import { Injectable } from '@angular/core';
import {
  PlannerActionsDropdownItem,
  PlannerSidebarData,
  PlannerSidebarMode,
} from '../components/layout/stacked-sidebar/components/planner-v2/planner-v2.interface';
import { ModalData } from '../model/modal-data.model';
import { SidebarData, SidebarType } from '../components/layout/stacked-sidebar/stacked-sidebar.interface';
import { StackedSidebarComponent } from '../components/layout/stacked-sidebar/stacked-sidebar.component';
import { takeUntil } from 'rxjs';
import { ModalService } from './modal.service';
import { PLANNER_ACTIONS_REGISTRY, PlannerActionContext } from '../interfaces/planner-table.interface';
import { PlannerService } from './planner.service';
import { RequestHelper } from './request-helper.service';

@Injectable({
  providedIn: 'root',
})
export class PlannerActionService {

  constructor(
    private readonly modalService: ModalService,
    private readonly plannerService: PlannerService,
    private readonly requestHelper: RequestHelper,
  ) {
  }

  updatePlanner(ctx: PlannerActionContext): void {
    const { items, destroy$, reloadTable } = ctx;

    const planner = items[0];

    const availableActions: PlannerActionsDropdownItem[] = [PLANNER_ACTIONS_REGISTRY[planner.action]];

    const modalData: ModalData<SidebarData<PlannerSidebarData>> = {
      component: StackedSidebarComponent,
      width: '50vw',
      hideHeader: true,
      data: {
        type: SidebarType.Planner,
        payload: {
          mode: PlannerSidebarMode.Update,
          scope: planner.scope,
          actions: availableActions,
          payloads: [planner.payload],
          planner_id: planner.id,
          title: planner.title,
          status: planner.status,
          action: planner.action,
          schedule: planner.schedule,
          occurrences_count: planner.occurrences_count,
        },
      },
    };
    this.modalService
      .openModal(modalData)
      .pipe(takeUntil(destroy$))
      .subscribe(() => {
        reloadTable?.();
      });
  }

  updatePlannerContent(ctx: PlannerActionContext): void {
  }

  activatePlanners(ctx: PlannerActionContext): void {
    this.updatePlannersStatus(ctx);
  }

  deactivatePlanners(ctx: PlannerActionContext): void {
    this.updatePlannersStatus(ctx);
  }

  updatePlannersStatus(ctx: PlannerActionContext): void {
    const {
      items,
      destroy$,
      status,
      errorMessageKey,
      reloadTable,
      clearSelection,
    } = ctx;

    if (!status) return;

    this.requestHelper.handle<void>(
      this.plannerService.updatePlannersStatus(items.map((v) => v.id), status),
      () => {
        clearSelection?.();
      },
      {
        destroy$: destroy$,
        defaultErrorMessage: errorMessageKey,
        onFinalize: reloadTable,
      },
    );
  }

  archivePlanners(ctx: PlannerActionContext): void {
    this.toggleArchivePlanners(ctx);
  }

  unarchivePlanners(ctx: PlannerActionContext): void {
    this.toggleArchivePlanners(ctx);
  }

  toggleArchivePlanners(ctx: PlannerActionContext): void {
    const {
      items,
      destroy$,
      active,
      errorMessageKey,
      reloadTable,
      clearSelection,
    } = ctx;

    this.requestHelper.handle<void>(
      this.plannerService.toggleArchivePlanners(items.map((v) => v.id), active!),
      () => {
        clearSelection?.();
      },
      {
        destroy$: destroy$,
        defaultErrorMessage: errorMessageKey,
        onFinalize: reloadTable,
      },
    );
  }

  runPlanners(ctx: PlannerActionContext): void {
    const {
      items,
      destroy$,
      errorMessageKey,
      reloadTable,
      clearSelection,
    } = ctx;

    this.requestHelper.handle<void>(
      this.plannerService.runPlanners(items.map((v) => v.id)),
      () => {
        clearSelection?.();
      },
      {
        destroy$: destroy$,
        defaultErrorMessage: errorMessageKey,
        onFinalize: reloadTable,
      },
    );
  }

  cancelPlanners(ctx: PlannerActionContext): void {
    const {
      items,
      destroy$,
      errorMessageKey,
      reloadTable,
      clearSelection,
    } = ctx;

    this.requestHelper.handle<void>(
      this.plannerService.cancelPlanners(items.map((v) => v.id)),
      () => {
        clearSelection?.();
      },
      {
        destroy$: destroy$,
        defaultErrorMessage: errorMessageKey,
        onFinalize: reloadTable,
      },
    );
  }
}
